// The caller's AI-credit balance — the client's only balance source (wiki/feature-ai-credits.md §"Reads").
// A live query over the AiCreditAccount read model: every balance change pushes 'AiCreditAccount' and refetches it.
import { findAiCreditAccountByUser, type AiCreditAccount, type FindAiCreditAccountByUserQuery } from '@voicedojo/backend-types';
import { ObservableQueryHandle, type ObservableQueryOptions } from '$lib/api/observableQuery.svelte';

export class CreditBalance {
  #query = $state<ObservableQueryHandle<FindAiCreditAccountByUserQuery, AiCreditAccount> | null>(null);
  #bound: string | null = null;
  readonly #options: ObservableQueryOptions;

  constructor(options: ObservableQueryOptions = {}) {
    this.#options = options;
  }

  // undefined = unknown (a guest, the first read, a failed read); null = no account opened yet.
  get #account(): AiCreditAccount | null | undefined {
    const q = this.#query;
    if (!q) return undefined;
    if (q.data.length > 0) return q.data[0];
    return q.loading || q.error ? undefined : null;
  }

  get paid(): number | null {
    const a = this.#account;
    return a === undefined ? null : (a?.aiCredits ?? 0);
  }

  get monthly(): number | null {
    const a = this.#account;
    return a === undefined ? null : (a?.monthlyCreditsIncludedRemaining ?? 0);
  }

  get total(): number | null {
    const a = this.#account;
    return a === undefined ? null : (a?.aiCredits ?? 0) + (a?.monthlyCreditsIncludedRemaining ?? 0);
  }

  // Impersonating reads the target's account but never subscribes: the hub groups on the admin (impersonation AC12.5).
  bind(userId: string | null, impersonating = false): void {
    const key = userId && `${userId}:${impersonating}`;
    if (key === this.#bound) return;
    this.#bound = key;
    this.#query?.stop();
    this.#query = null;
    if (!userId) return;
    const query = new ObservableQueryHandle(findAiCreditAccountByUser, {
      ...this.#options,
      ...(impersonating ? { isAuthenticated: () => false } : {}),
    });
    query.applyParams({});
    this.#query = query;
  }
}

export const creditBalance = new CreditBalance();
